import { useEffect, useMemo } from "react";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { queries } from "@corpuscomplete/core/zero";
import { Scope } from "@corpuscomplete/core/common/scope";

export namespace ScopeUI {
  export type Option = { id: string; label: string };

  export function useOptions(userId: string): Option[] {
    const [spaces] = useQuery(queries.space.byMembership());

    return useMemo(
      () => [
        { id: userId, label: "Personal" },
        ...spaces.map((space) => ({ id: space.id, label: space.name })),
      ],
      [spaces, userId],
    );
  }

  export function useLabel(userId: string, scopeId: string, fallback = "Scope"): string {
    const options = useOptions(userId);
    return options.find((option) => option.id === scopeId)?.label ?? fallback;
  }

  export function useAccessibleScope(
    scope: Scope.Info,
    userId: string,
    options: {
      onInaccessibleScope?: () => void;
    } = {},
  ): Scope.Info | null {
    const [spaces, details] = useQuery(queries.space.byMembership());
    const inaccessibleScope = details.type !== "unknown" && !Scope.isAccessible(scope, spaces);
    const accessibleScope = Scope.resolveAccessible(scope, spaces, userId);

    useEffect(() => {
      if (inaccessibleScope) {
        options.onInaccessibleScope?.();
      }
    }, [inaccessibleScope, options.onInaccessibleScope]);

    if (scope.kind === "space" && details.type === "unknown") {
      return null;
    }

    return accessibleScope;
  }
}
